import CharactersLayout from '#/components/layout/CharactersLayout/CharactersLayout';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import StoriesLayout from '#/components/layout/StoriesLayout/StoriesLayout';
import { TAppDispatch, TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { apiConfig } from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import ICharacter from '#/types/characters';
import IComic from '#/types/comics';
import IStory from '#/types/stories';
import generateAuthParams from '#/utils/generateHash';
import { IMarvelAPIResponse } from '#/types/common';
import { useNavigate } from 'react-router-dom';
import { RoutesNames } from '#/router/routes';
import { BookMarksActionType } from '#/redux/actions/bookmarks';
import ContextMenuLayer, {
  IContextMenuOption,
} from '#/components/layout/ContextMenuLayer/ContextMenuLayer';
import CharacterPreview from '#/components/layout/CharacterPreview/CharacterPreview';
import ComicPreview from '#/components/layout/ComicPreview/ComicPreview';
import StoryPreview from '#/components/layout/StoryPreview/StoryPreview';
import Button from '#/components/form/Button/Button';
import NoDataFound from '#/components/layout/NoDataFound/NoDataFound';
import { useError } from '#/hooks/useError/useError';

const Bookmarks = () => {
  const loadings = useRef(0);

  const bookmarksSelector = (state: TRootState) => state.bookmarks;

  const bookmarksState = useSelector(bookmarksSelector);

  const dispatch = useDispatch<TAppDispatch>();

  const { bookmarks, loading } = bookmarksState;

  const [characters, setCharacters] = useState<ICharacter[]>([]);

  const [comics, setComics] = useState<IComic[]>([]);

  const [stories, setStories] = useState<IStory[]>([]);

  const navigate = useNavigate();

  const { addError } = useError();

  const bookmarkedCharacters = useMemo(() => {
    return bookmarks.filter((bookmark) => bookmark.type === 'character');
  }, [bookmarks]);

  useEffect(() => {
    const getCharacter = async (id: number) => {
      loadings.current++;

      const apiManager = new ApiManager(apiConfig);

      const response = await apiManager.getEntity<
        IMarvelAPIResponse<ICharacter>
      >('CHARACTERS', id, generateAuthParams());

      loadings.current--;

      if (loadings.current === 0) {
        dispatch({ type: BookMarksActionType.BOOKMARKS_STOP_LOADING });
      }

      if ('error' in response) {
        addError(response.error);
      } else {
        if (response.data.data?.results) {
          const newCharacter = response.data.data.results[0];

          setCharacters((prevCharacters) => {
            const dontExists =
              prevCharacters.find(
                (prevCharacter) => prevCharacter.id === newCharacter.id
              ) === undefined;

            if (dontExists) {
              return [...prevCharacters, newCharacter];
            } else {
              return [...prevCharacters];
            }
          });
        }
      }
    };

    dispatch({ type: BookMarksActionType.BOOKMARKS_START_LOADING });

    if (bookmarkedCharacters.length > 0) {
      bookmarkedCharacters.forEach((bookmarkedCharacter) => {
        getCharacter(bookmarkedCharacter.id);
      });
    } else {
      dispatch({ type: BookMarksActionType.BOOKMARKS_STOP_LOADING });
    }

    return () => {
      setCharacters([]);
    };
  }, [addError, bookmarkedCharacters, dispatch]);

  const bookmarkedComics = useMemo(() => {
    return bookmarks.filter((bookmark) => bookmark.type === 'comic');
  }, [bookmarks]);

  useEffect(() => {
    const getComic = async (id: number) => {
      const apiManager = new ApiManager(apiConfig);

      loadings.current++;

      const response = await apiManager.getEntity<IMarvelAPIResponse<IComic>>(
        'COMICS',
        id,
        generateAuthParams()
      );

      loadings.current--;

      if (loadings.current === 0) {
        dispatch({ type: BookMarksActionType.BOOKMARKS_STOP_LOADING });
      }

      if ('error' in response) {
        addError(response.error);
      } else {
        if (response.data.data?.results) {
          const newComic = response.data.data.results[0];

          setComics((prevComics) => {
            const dontExists =
              prevComics.find((prevComic) => prevComic.id === newComic.id) ===
              undefined;
            if (dontExists) {
              return [...prevComics, newComic];
            } else {
              return [...prevComics];
            }
          });
        }
      }
    };

    dispatch({ type: BookMarksActionType.BOOKMARKS_START_LOADING });

    if (bookmarkedComics.length > 0) {
      bookmarkedComics.forEach((bookmarkedComic) =>
        getComic(bookmarkedComic.id)
      );
    } else {
      dispatch({ type: BookMarksActionType.BOOKMARKS_STOP_LOADING });
    }

    return () => {
      setComics([]);
    };
  }, [addError, bookmarkedComics, dispatch]);

  const bookmarkedStories = useMemo(() => {
    return bookmarks.filter((bookmark) => bookmark.type === 'story');
  }, [bookmarks]);

  useEffect(() => {
    const getStory = async (id: number) => {
      const apiManager = new ApiManager(apiConfig);

      loadings.current++;

      const response = await apiManager.getEntity<IMarvelAPIResponse<IStory>>(
        'STORIES',
        id,
        generateAuthParams()
      );

      loadings.current--;

      if (loadings.current === 0) {
        dispatch({ type: BookMarksActionType.BOOKMARKS_STOP_LOADING });
      }

      if ('error' in response) {
        addError(response.error);
      } else {
        if (response.data.data?.results) {
          const newStory = response.data.data.results[0];

          setStories((prevStories) => {
            const dontExists =
              prevStories.find((prevStory) => prevStory.id === newStory.id) ===
              undefined;

            if (dontExists) {
              return [...prevStories, newStory];
            } else {
              return [...prevStories];
            }
          });
        }
      }
    };

    dispatch({ type: BookMarksActionType.BOOKMARKS_START_LOADING });

    if (bookmarkedStories.length > 0) {
      bookmarkedStories.forEach((bookmarkedStory) =>
        getStory(bookmarkedStory.id)
      );
    } else {
      dispatch({ type: BookMarksActionType.BOOKMARKS_STOP_LOADING });
    }

    return () => {
      setStories([]);
    };
  }, [addError, bookmarkedStories, dispatch]);

  const handleRemoveAll = () => {
    dispatch({
      type: BookMarksActionType.BOOKMARKS_REMOVE_ALL_BOOKMARKS,
    });
  };

  return (
    <div>
      <h1>Bookmarks</h1>

      {bookmarks.length > 0 && (
        <Button
          type="danger"
          text="Remove all"
          handleOnClick={handleRemoveAll}
        />
      )}

      <h2>Characters</h2>

      {characters.length > 0 || loading ? (
        <CharactersLayout>
          {loading
            ? [...Array(5).keys()].map((loadingCharacter) => (
                <CharacterPreview key={loadingCharacter} skeleton />
              ))
            : characters.map((character) => {
                const characterIsBookmarked =
                  bookmarkedCharacters.find(
                    (bookmarkedCharacter) =>
                      bookmarkedCharacter.id === character.id
                  ) !== undefined;

                const handleCharacterClick = () =>
                  navigate(`${RoutesNames.CHARACTERS}/${character.id}`);

                const handleRemoveFromBookmark = () => {
                  if (character.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_REMOVE_BOOKMARK,
                      payload: { type: 'character', id: character.id },
                    });
                  }
                };

                const options: IContextMenuOption[] = [
                  {
                    text: 'Remove from bookmarks',
                    onClick: handleRemoveFromBookmark,
                  },
                ];

                return (
                  <ContextMenuLayer key={character.id} options={options}>
                    <CharacterPreview
                      handleOnClick={handleCharacterClick}
                      image={`${character.thumbnail?.path}/portrait_xlarge.${character.thumbnail?.extension}`}
                      name={character.name ? character.name : ''}
                      bookmarked={characterIsBookmarked}
                    />
                  </ContextMenuLayer>
                );
              })}
        </CharactersLayout>
      ) : (
        <NoDataFound />
      )}
      <h2>Comics</h2>
      {comics.length > 0 || loading ? (
        <CharactersLayout>
          {loading
            ? [...Array(5).keys()].map((loadingComic) => (
                <ComicPreview key={loadingComic} skeleton />
              ))
            : comics.map((comic) => {
                const comicIsBookmarked =
                  bookmarkedComics.find(
                    (bookmarkedComic) => bookmarkedComic.id === comic.id
                  ) !== undefined;

                const handleComicClick = () =>
                  navigate(`${RoutesNames.COMICS}/${comic.id}`);

                const handleRemoveFromBookmark = () => {
                  if (comic.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_REMOVE_BOOKMARK,
                      payload: { type: 'comic', id: comic.id },
                    });
                  }
                };

                const options: IContextMenuOption[] = [
                  {
                    text: 'Remove from bookmarks',
                    onClick: handleRemoveFromBookmark,
                  },
                ];

                const yearString = comic.dates?.find(
                  (date) => date.type === 'onsaleDate'
                )?.date;

                const year = yearString
                  ? new Date(yearString).getFullYear()
                  : new Date().getFullYear();

                return (
                  <ContextMenuLayer key={comic.id} options={options}>
                    <ComicPreview
                      bookmarked={comicIsBookmarked}
                      handleOnClick={handleComicClick}
                      image={`${comic.thumbnail?.path}/portrait_xlarge.${comic.thumbnail?.extension}`}
                      title={comic.title ? comic.title : ''}
                      year={year}
                    />
                  </ContextMenuLayer>
                );
              })}
        </CharactersLayout>
      ) : (
        <NoDataFound />
      )}
      <h2>Stories</h2>
      {stories.length > 0 || loading ? (
        <StoriesLayout>
          {loading
            ? [...Array(3).keys()].map((loadingStory) => (
                <StoryPreview key={loadingStory} skeleton />
              ))
            : stories.map((story) => {
                const storyIsBookmarked =
                  bookmarkedStories.find(
                    (bookmarkedStory) => bookmarkedStory.id === story.id
                  ) !== undefined;

                const handleStoryClick = () =>
                  navigate(`${RoutesNames.STORIES}/${story.id}`);

                const handleRemoveFromBookmark = () => {
                  if (story.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_REMOVE_BOOKMARK,
                      payload: { type: 'story', id: story.id },
                    });
                  }
                };

                const options: IContextMenuOption[] = [
                  {
                    text: 'Remove from bookmarks',
                    onClick: handleRemoveFromBookmark,
                  },
                ];

                return (
                  <ContextMenuLayer key={story.id} options={options}>
                    <StoryPreview
                      bookmarked={storyIsBookmarked}
                      title={story.title ? story.title : ''}
                      originalIssue={
                        story.originalIssue?.name
                          ? story.originalIssue?.name
                          : ''
                      }
                      handleOnClick={handleStoryClick}
                    />
                  </ContextMenuLayer>
                );
              })}
        </StoriesLayout>
      ) : (
        <NoDataFound />
      )}
    </div>
  );
};

export default Bookmarks;
